import React, { useState, useEffect } from 'react';
import '../styles/NotificationSettings.css';

// NOTE: this assumes the same auth pattern as your other admin components —
// a JWT stored in localStorage under 'token', sent as a Bearer header.
// If your other components (e.g. Inquiries.js) use a different helper for
// authenticated fetches, swap the fetch calls below to match that helper
// instead of duplicating this pattern.

const API_BASE = '/api/notification-rules';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
}

export default function NotificationSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load rules');
      const data = await res.json();
      setRules(data.rules);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateRule(id, patch) {
    setSavingId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(patch)
      });
      if (!res.ok) throw new Error('Failed to save rule');
      const data = await res.json();
      setRules((prev) => prev.map((r) => (r.id === id ? data.rule : r)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  }

  async function deleteRule(id) {
    if (!window.confirm('Delete this notification rule?')) return;
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Failed to delete rule');
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function addRule(event_type) {
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          event_type,
          offset_days: 1,
          message_template:
            event_type === 'arrival'
              ? 'Guest {guest_name} arrives in {days} day(s) — {check_in}.'
              : 'Guest {guest_name} leaves in {days} day(s) — {check_out}.'
        })
      });
      if (!res.ok) throw new Error('Failed to add rule');
      const data = await res.json();
      setRules((prev) => [...prev, data.rule]);
    } catch (err) {
      setError(err.message);
    }
  }

  function renderGroup(type, title) {
    const group = rules.filter((r) => r.event_type === type);
    return (
      <div className="notif-group">
        <div className="notif-group-header">
          <h3>{title}</h3>
          <button className="notif-add-btn" onClick={() => addRule(type)}>
            + Add rule
          </button>
        </div>
        {group.length === 0 && <p className="notif-empty">No rules yet.</p>}
        {group.map((rule) => (
          <div className="notif-rule-card" key={rule.id}>
            <div className="notif-rule-row">
              <label>
                Send
                <input
                  type="number"
                  min="0"
                  value={rule.offset_days}
                  onChange={(e) =>
                    setRules((prev) =>
                      prev.map((r) =>
                        r.id === rule.id ? { ...r, offset_days: Number(e.target.value) } : r
                      )
                    )
                  }
                  onBlur={(e) => updateRule(rule.id, { offset_days: Number(e.target.value) })}
                />
                day(s) before
              </label>
              <label className="notif-toggle">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(e) => updateRule(rule.id, { enabled: e.target.checked })}
                />
                Enabled
              </label>
              <button className="notif-delete-btn" onClick={() => deleteRule(rule.id)}>
                Delete
              </button>
            </div>
            <textarea
              className="notif-template-input"
              value={rule.message_template}
              rows={2}
              onChange={(e) =>
                setRules((prev) =>
                  prev.map((r) =>
                    r.id === rule.id ? { ...r, message_template: e.target.value } : r
                  )
                )
              }
              onBlur={(e) => updateRule(rule.id, { message_template: e.target.value })}
            />
            {savingId === rule.id && <span className="notif-saving">Saving…</span>}
          </div>
        ))}
      </div>
    );
  }

  if (loading) return <div className="notif-settings">Loading notification rules…</div>;

  return (
    <div className="notif-settings">
      <h2>Notification Settings</h2>
      <p className="notif-hint">
        Placeholders available in templates: <code>{'{guest_name}'}</code>{' '}
        <code>{'{check_in}'}</code> <code>{'{check_out}'}</code> <code>{'{days}'}</code>
      </p>
      {error && <div className="notif-error">{error}</div>}
      {renderGroup('arrival', 'Guest Arrival Schedule')}
      {renderGroup('departure', 'Guest Departure Schedule')}
    </div>
  );
}
